import type { ComponentProps } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as SelectPrimitive from '@radix-ui/react-select';
import { faAngleDown, faAngleUp } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCheck } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { CSS } from '@components-shared/src/stitches';
import { styled, theme } from '@components-shared/src/stitches';

export type ItemProps = Partial<ComponentProps<typeof Item>>;

export type SelectProps = {
  onValueChange: (value: string) => void;
  value: string;
  placeholder?: string;
  hasError?: boolean;
  contentCss?: CSS;
  triggerCss?: CSS;
  /**
   * @deprecated Use css variants instead
   */
  deprecatedTriggerProps?: Partial<ComponentProps<typeof StyledTrigger>>;
  disabled?: boolean;
  flexItem?: boolean;
  children: React.ReactNode;
  itemProps?: ItemProps;
} & ComponentProps<typeof StyledRoot>;

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      children,
      hasError,
      disabled,
      contentCss,
      triggerCss,
      flexItem,
      itemProps,
      deprecatedTriggerProps,
      ...props
    },
    forwardedRef,
  ) => {
    const displayChildren = React.useMemo(() => {
      return !!props.placeholder ? (
        <>
          <Item value="" disabled={disabled} {...itemProps}>
            {' '}
            {props.placeholder ?? ''}
          </Item>
          {children}
        </>
      ) : (
        <StyledChildren>{children}</StyledChildren>
      );
    }, [children, props.placeholder, disabled, itemProps]);
    return (
      <StyledRoot {...props}>
        <StyledTrigger
          ref={forwardedRef}
          css={{ ...triggerCss }}
          hasError={!!hasError}
          disabled={disabled}
          {...deprecatedTriggerProps}
        >
          <SelectPrimitive.Value />
          <SelectPrimitive.Icon>
            <FontAwesomeIcon icon={faAngleDown} />
          </SelectPrimitive.Icon>
        </StyledTrigger>
        <SelectPrimitive.Portal>
          <StyledContent css={contentCss} flexItem={flexItem} position="popper" sideOffset={10}>
            <SelectPrimitive.ScrollUpButton className="flex h-6 cursor-default items-center justify-center">
              <FontAwesomeIcon icon={faAngleUp} />
            </SelectPrimitive.ScrollUpButton>
            <StyledViewport>{displayChildren}</StyledViewport>
            <SelectPrimitive.ScrollDownButton className="flex h-6 cursor-default items-center justify-center ">
              <FontAwesomeIcon icon={faAngleDown} />
            </SelectPrimitive.ScrollDownButton>
          </StyledContent>
        </SelectPrimitive.Portal>
      </StyledRoot>
    );
  },
);
Select.displayName = 'Select';

const Item = React.forwardRef<
  HTMLDivElement,
  { value: string; disabled?: boolean; showIcon?: boolean; children: React.ReactNode } & Partial<
    ComponentProps<typeof StyledItem>
  >
>(({ children, disabled, showIcon = true, ...props }, forwardedRef) => {
  return (
    <StyledItem {...props} ref={forwardedRef} disabled={disabled}>
      {showIcon && (
        <IconWrapper>
          <StyledItemIndicator>
            {disabled}
            <FontAwesomeIcon icon={faCheck} color={theme.colors.brand300.value} size="sm" />
          </StyledItemIndicator>
        </IconWrapper>
      )}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </StyledItem>
  );
});
Item.displayName = 'Item';

export { Select, Item };

const StyledRoot = styled(SelectPrimitive.Root, {
  display: 'flex',
  variants: {
    flexItem: {
      true: {
        justifyContent: 'space-between',
      },
    },
  },
});

const StyledChildren = styled('div', {
  width: '100%',
  span: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    paddingX: '12px',
  },
});

const StyledContent = styled(SelectPrimitive.Content, {
  position: 'relative',
  zIndex: '$modalDropdown',
  overflow: 'hidden',
  backgroundColor: '#fff',
  border: '1px solid $neutral300',
  borderRadius: 4,
  width: 'var(--radix-select-trigger-width)',
  maxHeight: 'var(--radix-select-content-available-height)',
  variants: {
    flexItem: {
      true: {
        display: 'flex',
        justifyContent: 'space-between',
      },
    },
  },
});

export const BASE_500_WIDTH = 336;

const StyledTrigger = styled(SelectPrimitive.SelectTrigger, {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  borderRadius: 4,
  border: '1px solid $neutral300',
  height: 38,
  pointerEvents: 'auto',
  '&:hover': {
    cursor: 'pointer',
  },

  '@tablet': {
    height: 40,
  },

  variants: {
    hasError: {
      true: {
        border: '1px solid $destructive700',
      },
    },
    disabled: {
      true: {
        pointerEvents: 'none',
        color: '$neutral400',
      },
    },
    base500Background: {
      true: {
        background: '$base100',
        width: BASE_500_WIDTH,
        border: 'unset',
        fontWeight: 500,
      },
    },
  },
});

const StyledViewport = styled(SelectPrimitive.Viewport, {
  padding: '10px 5px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  variants: {
    flexItem: {
      true: {
        flexDirection: 'row',
      },
    },
  },
});

const StyledItem = styled(SelectPrimitive.Item, {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'space-between',
  width: '100%',
  fontSize: 16,
  lineHeight: 2,
  borderRadius: 3,
  height: 28,
  position: 'relative',
  userSelect: 'none',
  base: 'M',

  '@tablet': {
    base: 'L',
  },
  '&:hover,&:focus': {
    backgroundColor: '$neutral100',
    cursor: 'pointer',
  },
  variants: {
    disabled: {
      true: {
        backgroundColor: '#fff',
        pointerEvents: 'none',
        color: '$neutral300',
        '&:hover,&:focus': {
          backgroundColor: '#fff',
        },
      },
    },
    fontWeight500: {
      true: {
        fontWeight: 500,
      },
    },
  },
});

const StyledItemIndicator = styled(SelectPrimitive.ItemIndicator, {
  left: 0,
  width: 25,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: 6,
});

const IconWrapper = styled('div', {
  width: 25,
});
